import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiBriefcase,
  FiCalendar,
  FiClock,
  FiUser,
} from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import {
  useGlobalFilter,
  usePagination,
  useTable,
} from "react-table";
import AxiosInstance from "../Home/AxiosInstance";
import Footers from "../Home/Footers";
import TraineeHeader from "./TraineeHeader";
import "../Home/theme.css";
import "./TraineeProfile.css";

export const statusClass = (value) => {
  const v = String(value || "").toLowerCase();
  if (
    v.includes("enrolled") ||
    v === "yes" ||
    v.includes("accepted") ||
    v.includes("selected") ||
    v.includes("offer")
  ) {
    return "tp-badge ok";
  }
  if (
    v.includes("interview") ||
    v.includes("pending") ||
    v.includes("review") ||
    v.includes("applied")
  ) {
    return "tp-badge warn";
  }
  if (v.includes("reject") || v.includes("withdraw") || v === "no") {
    return "tp-badge bad";
  }
  return "tp-badge";
};

export const getInstructorName = (enrollment) => {
  const user = enrollment.batch?.course_material?.user;
  const trainer = enrollment.batch?.course_material?.trainer;
  if (user) return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  if (trainer)
    return `${trainer.first_name || ""} ${trainer.last_name || ""}`.trim();
  return "Instructor";
};

export const getCourseLogo = (enrollment) => {
  const logo = enrollment.batch?.course_material?.course?.course_logo;
  if (!logo) return null;
  if (String(logo).startsWith("http")) return logo;
  return `${AxiosInstance.defaults.baseURL}media/${logo}`;
};

export const sortByDateDesc = (items, getDate) =>
  [...(items || [])].sort((a, b) => {
    const da = new Date(getDate(a) || 0).getTime();
    const db = new Date(getDate(b) || 0).getTime();
    return db - da;
  });

export const openCourse = (navigate, enrollment) => {
  const batchId = enrollment.batch?.batch_id;
  const courseMaterialId =
    enrollment.batch?.course_material?.course_material_id;
  if (!batchId) return;
  localStorage.setItem("batch_id", batchId);
  if (courseMaterialId) {
    localStorage.setItem("course_material_id", courseMaterialId);
  }
  navigate(`/my-courses/material/${batchId}`);
};

const catalogState = { from: "trainee-profile" };

function ListPageShell({ title, subtitle, children }) {
  const navigate = useNavigate();
  return (
    <div className="TraineePage als-trainee-dash">
      <TraineeHeader />
      <main className="tp-body tp-body--list">
        <section className="tp-panel">
          <div className="tp-panel-head">
            <div className="tp-title-row">
              <button
                type="button"
                className="tp-back-icon"
                onClick={() => navigate("/trainee-profile")}
                aria-label="Back to dashboard"
                title="Back to dashboard"
              >
                <FiArrowLeft />
              </button>
              <h2>{title}</h2>
            </div>
            <p>{subtitle}</p>
          </div>
          {children}
        </section>
      </main>
      <Footers />
    </div>
  );
}

export function TraineeEnrollmentsPage() {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [courseInput, setCourseInput] = useState("");
  const [trainerInput, setTrainerInput] = useState("");
  const [courseQuery, setCourseQuery] = useState("");
  const [trainerQuery, setTrainerQuery] = useState("");

  useEffect(() => {
    const traineeId = localStorage.getItem("traineeId");
    if (!traineeId) {
      alert("Trainee ID is missing. Please log in.");
      return;
    }
    AxiosInstance.get(`companies/trainee/enrollments/${traineeId}/`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setEnrollments(
          sortByDateDesc(data, (item) => item.enrolled_at || item.created_at)
        );
      })
      .catch((err) => {
        console.error("Error fetching enrollments:", err);
        setEnrollments([]);
      });
  }, []);

  const columns = useMemo(
    () => [
      { Header: "Course", accessor: "batch.course_material.course.course_name" },
      { Header: "Instructor", id: "instructor", accessor: getInstructorName },
    ],
    []
  );

  const filterFn = (rows, _ids, filterValue) =>
    rows.filter((row) => {
      const courseName =
        row.original.batch?.course_material?.course?.course_name?.toLowerCase() ||
        "";
      const instructor = getInstructorName(row.original).toLowerCase();
      return (
        courseName.includes(filterValue.course) &&
        instructor.includes(filterValue.trainer)
      );
    });

  const {
    page,
    setGlobalFilter,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: enrollments,
      initialState: {
        globalFilter: { course: "", trainer: "" },
        pageSize: 9,
      },
      globalFilter: filterFn,
    },
    useGlobalFilter,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter({
      course: courseQuery.toLowerCase(),
      trainer: trainerQuery.toLowerCase(),
    });
  }, [courseQuery, trainerQuery, setGlobalFilter]);

  return (
    <ListPageShell
      title="My enrollments"
      subtitle="All courses you have enrolled in, newest first."
    >
      <div className="tp-toolbar">
        <div className="tp-filter">
          <input
            type="text"
            value={courseInput}
            onChange={(e) => setCourseInput(e.target.value)}
            placeholder="Search by course name..."
          />
          {(courseInput || courseQuery) && (
            <button
              type="button"
              className="tp-clear"
              aria-label="Clear course filter"
              onClick={() => {
                setCourseInput("");
                setCourseQuery("");
              }}
            >
              <FaTimes size={11} />
            </button>
          )}
        </div>
        <div className="tp-filter">
          <input
            type="text"
            value={trainerInput}
            onChange={(e) => setTrainerInput(e.target.value)}
            placeholder="Search by instructor..."
          />
          {(trainerInput || trainerQuery) && (
            <button
              type="button"
              className="tp-clear"
              aria-label="Clear instructor filter"
              onClick={() => {
                setTrainerInput("");
                setTrainerQuery("");
              }}
            >
              <FaTimes size={11} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="tp-btn-primary"
          onClick={() => {
            setCourseQuery(courseInput);
            setTrainerQuery(trainerInput);
          }}
        >
          Search
        </button>
      </div>

      {page.length === 0 ? (
        <div className="tp-empty-state">
          <FiBookOpen />
          <h3>No enrollments yet</h3>
          <p>Browse courses and enroll to start learning.</p>
          <Link to="/courses" state={catalogState} className="tp-btn-primary">
            Browse courses
          </Link>
        </div>
      ) : (
        <div className="tp-course-grid">
          {page.map((row) => {
            const enrollment = row.original;
            const courseName =
              enrollment.batch?.course_material?.course?.course_name || "Course";
            const batchName = enrollment.batch?.batch_name;
            const instructor = getInstructorName(enrollment);
            const logo = getCourseLogo(enrollment);
            const enrolledAt = enrollment.enrolled_at
              ? new Date(enrollment.enrolled_at).toLocaleDateString()
              : null;

            return (
              <article
                key={enrollment.enrollment_id || row.id}
                className="tp-course-card"
              >
                <div
                  className="tp-course-media"
                  style={
                    !logo
                      ? {
                          background:
                            "linear-gradient(135deg, #5b5fff22, #0b0e2722)",
                        }
                      : undefined
                  }
                >
                  {logo ? <img src={logo} alt="" /> : <span>{courseName.charAt(0)}</span>}
                </div>
                <div className="tp-course-body">
                  <div className="tp-course-top">
                    <span className={statusClass(enrollment.is_enrolled)}>
                      {String(enrollment.is_enrolled || "Enrolled")}
                    </span>
                  </div>
                  <h3>{courseName}</h3>
                  <p className="tp-course-meta">
                    <FiUser /> {instructor}
                    {batchName ? ` · ${batchName}` : ""}
                  </p>
                  {enrolledAt && (
                    <p className="tp-course-meta">
                      <FiCalendar /> Enrolled {enrolledAt}
                    </p>
                  )}
                  <button
                    type="button"
                    className="tp-btn-primary tp-course-cta"
                    onClick={() => openCourse(navigate, enrollment)}
                  >
                    Continue learning <FiArrowRight />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {pageOptions.length > 1 && (
        <div className="tp-pagination">
          <button type="button" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </button>
          <span>
            Page <strong>{pageIndex + 1}</strong> of{" "}
            <strong>{pageOptions.length}</strong>
          </span>
          <button type="button" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </button>
        </div>
      )}
    </ListPageShell>
  );
}

function ApplicationsListPage({
  title,
  subtitle,
  emptyTitle,
  emptyBody,
  browseTo,
  browseLabel,
  apiPath,
  roleAccessor,
  detailPath,
}) {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [roleInput, setRoleInput] = useState("");
  const [statusInput, setStatusInput] = useState("");
  const [filter, setFilter] = useState({ role: "", status: "" });

  useEffect(() => {
    const traineeId = localStorage.getItem("traineeId");
    if (!traineeId) {
      alert("Trainee ID is missing. Please log in.");
      return;
    }
    AxiosInstance.get(`${apiPath}${traineeId}/`)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setApplications(
          sortByDateDesc(data, (item) => item.application_date || item.created_at)
        );
      })
      .catch((err) => {
        console.error("Error fetching applications:", err);
        setApplications([]);
      });
  }, [apiPath]);

  const columns = useMemo(
    () => [
      { Header: "Role", accessor: roleAccessor },
      { Header: "Status", accessor: "status" },
      { Header: "Date", accessor: "application_date" },
    ],
    [roleAccessor]
  );

  const filterFn = (rows, _ids, filterValue) =>
    rows.filter((row) => {
      const role = String(
        roleAccessor.split(".").reduce((obj, key) => obj?.[key], row.original) ||
          ""
      ).toLowerCase();
      const status = (row.original.status || "").toLowerCase();
      return (
        role.includes(filterValue.role) &&
        status.includes(filterValue.status)
      );
    });

  const {
    page,
    setGlobalFilter,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: applications,
      initialState: {
        globalFilter: { role: "", status: "" },
        pageSize: 10,
      },
      globalFilter: filterFn,
    },
    useGlobalFilter,
    usePagination
  );

  useEffect(() => {
    setGlobalFilter({
      role: filter.role.toLowerCase(),
      status: filter.status.toLowerCase(),
    });
  }, [filter, setGlobalFilter]);

  return (
    <ListPageShell title={title} subtitle={subtitle}>
      <div className="tp-toolbar">
        <div className="tp-filter">
          <input
            type="text"
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
            placeholder="Search by role..."
          />
          {(roleInput || filter.role) && (
            <button
              type="button"
              className="tp-clear"
              aria-label="Clear role filter"
              onClick={() => {
                setRoleInput("");
                setFilter((prev) => ({ ...prev, role: "" }));
              }}
            >
              <FaTimes size={11} />
            </button>
          )}
        </div>
        <div className="tp-filter">
          <input
            type="text"
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value)}
            placeholder="Search by status..."
          />
          {(statusInput || filter.status) && (
            <button
              type="button"
              className="tp-clear"
              aria-label="Clear status filter"
              onClick={() => {
                setStatusInput("");
                setFilter((prev) => ({ ...prev, status: "" }));
              }}
            >
              <FaTimes size={11} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="tp-btn-primary"
          onClick={() =>
            setFilter({
              role: roleInput,
              status: statusInput,
            })
          }
        >
          Search
        </button>
      </div>

      {page.length === 0 ? (
        <div className="tp-empty-state">
          <FiBriefcase />
          <h3>{emptyTitle}</h3>
          <p>{emptyBody}</p>
          <Link to={browseTo} state={catalogState} className="tp-btn-primary">
            {browseLabel}
          </Link>
        </div>
      ) : (
        <ul className="tp-app-list">
          {page.map((row) => {
            const app = row.original;
            const role =
              roleAccessor.split(".").reduce((obj, key) => obj?.[key], app) ||
              "Application";
            const idPath = detailPath(app);
            const applied = app.application_date
              ? new Date(app.application_date).toLocaleDateString()
              : null;
            const resumeUrl = app.resume_path
              ? `${AxiosInstance.defaults.baseURL}media/${app.resume_path}`
              : null;

            return (
              <li key={app.application_id || row.id} className="tp-app-item">
                <div className="tp-app-main">
                  <button
                    type="button"
                    className="tp-link-btn tp-app-role"
                    onClick={() =>
                      idPath && navigate(idPath, { state: catalogState })
                    }
                  >
                    {role}
                  </button>
                  <div className="tp-app-meta">
                    {applied && (
                      <span>
                        <FiClock /> Applied {applied}
                      </span>
                    )}
                    {resumeUrl && (
                      <a
                        href={resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tp-text-link"
                      >
                        View resume
                      </a>
                    )}
                  </div>
                  {app.feedback && (
                    <p className="tp-app-feedback">{app.feedback}</p>
                  )}
                </div>
                <div className="tp-app-status">
                  <span className={statusClass(app.status)}>
                    {app.status || "—"}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {pageOptions.length > 1 && (
        <div className="tp-pagination">
          <button type="button" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </button>
          <span>
            Page <strong>{pageIndex + 1}</strong> of{" "}
            <strong>{pageOptions.length}</strong>
          </span>
          <button type="button" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </button>
        </div>
      )}
    </ListPageShell>
  );
}

export function TraineeInternshipApplicationsPage() {
  return (
    <ApplicationsListPage
      title="Internship applications"
      subtitle="All internship roles you applied to, newest first."
      emptyTitle="No applications yet"
      emptyBody="Find internships and apply to start tracking here."
      browseTo="/internships"
      browseLabel="Browse internships"
      apiPath="companies/applications/"
      roleAccessor="internship.role"
      detailPath={(app) =>
        app.internship?.internship_id
          ? `/internships/${app.internship.internship_id}`
          : null
      }
    />
  );
}

export function TraineeJobApplicationsPage() {
  return (
    <ApplicationsListPage
      title="Job applications"
      subtitle="All jobs you applied to, newest first."
      emptyTitle="No job applications yet"
      emptyBody="Find jobs and apply to start tracking here."
      browseTo="/jobs"
      browseLabel="Browse jobs"
      apiPath="companies/job-applications/"
      roleAccessor="job.role"
      detailPath={(app) =>
        app.job?.job_id ? `/jobs/${app.job.job_id}` : null
      }
    />
  );
}
